// Annual document transport and payloads; shared by previews and signature requests.
const postAnnualPdfRequest = async (payload, token) => {
  const response = await fetch(functionUrl("annualProtocolPdfHttp"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(await window.PlummeAppCheck?.headers?.()),
    },
    body: JSON.stringify({
      ...payload,
      businessId: runtime.activeBusinessId,
      locale: window.PlummeI18n?.getLocale?.() === "en" ? "en" : "nb",
      generatedAtLabel: new Date().toLocaleString("nb-NO"),
    }),
  });
  const responseText = await response.text();
  const result = responseText.trim() ? JSON.parse(responseText) : null;
  if (!response.ok)
    throw new Error(
      result?.message || result?.error || `HTTP ${response.status}`,
    );
  if (!result?.pdfBase64) throw new Error(t("svc.noPdfReturned"));
  const url = URL.createObjectURL(pdfBase64ToBlob(result.pdfBase64));
  const link = document.createElement("a");
  link.href = url;
  link.download = result.fileName || "dokument.pdf";
  link.click();
  URL.revokeObjectURL(url);
  return result;
};
// Dokumentene bygges paa backend, som eier reglene for beslutningsdyktighet,
// disponering og oppstillingsplan. Klienten sender bare tallene og fakta.
// Grunnlaget backend trenger for aa bygge dokumentet. Samme payload brukes ved
// nedlasting og ved utsending til signering, saa mottakeren aapner nøyaktig det
// dokumentet brukeren saa.
const buildAnnualDocumentPayload = ({ documentKey, year }) => {
  const meetings = window.PlummeAnnualMeetings;
  if (!meetings)
    throw new Error(
      t("svc.protocolUnavailable", "Protokollmodulen er ikke lastet."),
    );
  const state = mergeState(runtime.appState);
  const annualModel = getAnnualAccountsModel({ year });
  const profile = runtime.userProfile || {};

  // Selve aarsregnskapet trenger fjoraaret i egen kolonne, saa modellen bygges
  // for begge aarene. Protokollene trenger bare aarsresultatet.
  if (documentKey === "pdf") {
    const previousModel = buildAnnualAccountsModel({
      year: year - 1,
      trialRows: annualModel.process?.comparativeTrialBalance?.rows,
    });
    const workYearsNote = safeArray(annualModel.notes).find(
      (note) => note.id === "work-years",
    );
    return {
      documentKey,
      year,
      company: {
        name: annualModel.company?.name || "",
        organizationNumber:
          annualModel.company?.orgnr ||
          annualModel.company?.organizationNumber ||
          "",
      },
      current: {
        incomeStatement: annualModel.incomeStatement,
        balanceSheet: annualModel.balanceSheet,
      },
      previous: {
        incomeStatement: previousModel.incomeStatement,
        balanceSheet: previousModel.balanceSheet,
      },
      notes: annualModel.notes,
      reportContext: {
        jurisdiction: annualModel.accountingScope?.jurisdiction,
        legalFormCode: annualModel.accountingScope?.legalFormCode,
        year,
        framework: annualModel.submission.accountingRuleType,
        period: annualModel.submission.accountingPeriod,
        process: annualModel.process,
        meetings: state.annualMeetingsByYear?.[year] || {},
        yearClosed: annualModel.status.yearClosed,
      },
      workYearsLabel: workYearsNote
        ? String(workYearsNote.workYears ?? "")
        : "",
    };
  }

  const payload = meetings.buildProtocolRequest({
    documentKey,
    year,
    company: annualModel.company,
    people: state.companyRoles,
    meetings: state.annualMeetingsByYear?.[year],
    shareholders: state.shareholders,
    annualResult: annualModel.incomeStatement?.annualResult,
    createdBy: {
      name: profile.displayName || "",
      email: profile.email || runtime.auth.currentUser?.email || "",
    },
  });
  return {
    ...payload,
    annualReport: buildAnnualDocumentPayload({ documentKey: "pdf", year }),
  };
};

const downloadAnnualProtocolPdf = async ({ documentKey, year } = {}) => {
  const token = await runtime.auth.currentUser?.getIdToken();
  if (!token) throw new Error(t("svc.mustBeSignedIn3"));
  return postAnnualPdfRequest(
    buildAnnualDocumentPayload({ documentKey, year }),
    token,
  );
};

// Sender dokumentet til signering. Backend lager tokenene og eposten; klienten
// sender bare hvem som skal signere.
const sendAnnualSignatureRequests = async ({ documentKey, year } = {}) => {
  const roles = window.PlummeCompanyRoles;
  const meetings = window.PlummeAnnualMeetings;
  if (!roles)
    throw new Error(
      t("svc.protocolUnavailable", "Protokollmodulen er ikke lastet."),
    );
  const state = mergeState(runtime.appState);
  const annualModel = getAnnualAccountsModel({ year });
  const signers =
    meetings?.generalMeetingSigners?.({
      documentKey,
      people: state.companyRoles,
      meeting: state.annualMeetingsByYear?.[year]?.[documentKey],
    }) || roles.signersForDocument(state.companyRoles, documentKey);
  if (!signers.length)
    throw new Error(t("clo.signersEmpty", "Ingen roller er registrert ennå."));

  // Dokumentet bygges og lagres av backend naar det sendes, saa alle signerer
  // samme fil. Grunnlaget er det samme som ved nedlasting.
  const result = await authorizedPost("sendAnnualSignatureRequestsHttp", {
    businessId: runtime.activeBusinessId,
    document: buildAnnualDocumentPayload({ documentKey, year }),
    locale: window.PlummeI18n?.getLocale?.() === "en" ? "en" : "nb",
    generatedAtLabel: new Date().toLocaleString("nb-NO"),
    signers: signers.map((person) => ({
      personId: person.id,
      name: person.name,
      email: person.email,
      roleLabel: roles.roleLabel(roles.primaryRole(person)),
      bornAt: meetings ? meetings.toDisplayDate(person.bornAt) : person.bornAt,
    })),
  });
  return result;
};

const sendAnnualSignatureReminders = async ({ documentKey, year } = {}) =>
  authorizedPost("sendAnnualSignatureRemindersHttp", {
    businessId: runtime.activeBusinessId,
    documentKey,
    year,
  });

const getAnnualSignatureStatus = async ({
  year,
  documentKey,
  download = false,
} = {}) =>
  authorizedPost("annualSignatureStatusHttp", {
    businessId: runtime.activeBusinessId,
    year,
    documentKey,
    download,
    documents: Object.fromEntries(
      ["pdf", "board", "general"].map((key) => [
        key,
        buildAnnualDocumentPayload({ documentKey: key, year }),
      ]),
    ),
  });

const downloadAnnualSignatureDocument = async ({ year, documentKey }) => {
  const result = await getAnnualSignatureStatus({
    year,
    documentKey,
    download: true,
  });
  const url = URL.createObjectURL(pdfBase64ToBlob(result.pdfBase64));
  const link = document.createElement("a");
  link.href = url;
  link.download = result.fileName;
  link.click();
  URL.revokeObjectURL(url);
  return result;
};

// Loaded by backend.jsx in the shared classic-script scope.
